import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createAd,
  getAds,
  getAdById,
  updateAd,
  deleteAd,
  createSlot,
  getSlots,
  getSlotById,
  updateSlot,
  deleteSlot,
  getSlotAllocations,
  allocateSlot,
  deallocateSlot,
  calculateAdvertisementImpact,
  getAllocateSlots,
} from "../api/advertisement";

// Advertisement Hooks
export const useCreateAd = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => createAd(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["ads"]);
    },
  });
};

export const useGetAds = () => {
  return useQuery({
    queryKey: ["ads"],
    queryFn: () => getAds(),
    refetchOnWindowFocus: false,
  });
};

export const useGetAdById = (id) => {
  return useQuery({
    queryKey: ["ad", id],
    queryFn: () => getAdById(id),
    enabled: !!id,
  });
};

export const useUpdateAd = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => updateAd(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["ads"]);
      queryClient.invalidateQueries(["ad", id]);
    },
  });
};

export const useDeleteAd = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteAd(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["ads"]);
    },
  });
};

// Ad Slots Hooks
export const useCreateSlot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => createSlot(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["slots"]);
    },
  });
};

export const useGetSlots = () => {
  return useQuery({
    queryKey: ["slots"],
    queryFn: () => getSlots(),
    refetchOnWindowFocus: false,
  });
};

export const useGetSlotById = (id) => {
  return useQuery({
    queryKey: ["slot", id],
    queryFn: () => getSlotById(id),
    enabled: !!id,
  });
};

export const useUpdateSlot = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => updateSlot(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["slots"]);
      queryClient.invalidateQueries(["slot", id]);
    },
  });
};

export const useDeleteSlot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteSlot(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["slots"]);
      queryClient.invalidateQueries(["slot-allocations"]);
    },
  });
};

// Slot Allocation Hooks
export const useGetSlotAllocations = () => {
  return useQuery({
    queryKey: ["slot-allocations"],
    queryFn: () => getSlotAllocations(),
    refetchOnWindowFocus: false,
  });
};

export const useAllocateSlot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => allocateSlot(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["slot-allocations"]);
      queryClient.invalidateQueries(["slots"]);
    },
  });
};

export const useDeallocateSlot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deallocateSlot(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["slot-allocations"]);
      queryClient.invalidateQueries(["slots"]);
    },
  });
};

export const useGetAllocatedSlots = () => {
  return useQuery({
    queryKey: ['allocated-slots'],
    queryFn: () => getAllocateSlots(),
  });
}

// Generic Hook for Advertisement Impact based on location
export const useGetAttachedProductImpact = (location, itemId) => {
  return useQuery({
    queryKey: ['getAttachedProductImpact', location, itemId],
    queryFn: () => calculateAdvertisementImpact(location, itemId),
    enabled: !!location && !!itemId,
  });
};
